import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Request from 'files/Request.js';
import { parseOffer, isOfferLive } from 'files/LimitedOffer.js';
import ConsultationModal from 'views/ConsultationModal.js';
import NotifyModal from 'views/NotifyModal.js';

// ---------------------------------------------------------------------------
// Geofencing / serviceability gate.
//
// On load we ask the server (IP-based, no user input) whether the visitor is in
// an area where we support ONLINE SALES and/or have an AUTHORIZED DEALER:
//
//   Request.post(utils, '/shop/', { type: 'is_serviceable_area' })
//     -> { online_sales: bool, dealer: bool }
//
// That resolves to one of four states, consumed everywhere via useServiceability():
//
//   loading  — check in flight; pricing is withheld (skeleton)
//   online   — online sales supported -> show pricing + cart as normal
//   dealer   — no online sales, but a dealer is nearby -> replace pricing with
//              "Check availability" (routes into an in-home consultation) and
//              surface the <Consultation/> section on the homepage
//   waitlist — no online sales and no dealer -> replace pricing with a
//              "Notify me" capture (we're expanding; this tells us where)
//   error    — check failed -> FAIL CLOSED (treated like a gate; generic
//              "Check availability" so we never leak pricing on a hiccup)
//
// Only `online` unlocks pricing. Everything else hides it.
//
// The same response carries which storefront discounts are live (online areas only):
//   { ..., limited_offer: { title, starts_at, ends_at }, volume_pricing: true|false }
// `offer` is exposed only while its window is open (see files/LimitedOffer.js);
// `volumePricing` is true only while the Water Sensor volume discount is active
// in Shopify, so multi-pack pricing is never shown when checkout won't charge it.
// ---------------------------------------------------------------------------

const ServiceabilityContext = createContext({
    status: 'loading',
    canBuyOnline: false,
    hasDealer: false,
    offer: null,
    volumePricing: false,
    openAvailability: () => {},
});

// Longest delay setTimeout honors (~24.8 days); longer waits just re-arm.
const MAX_TIMEOUT = 2147483647;

// Development only: `?offer_preview` fakes an offer ending in two days, so the
// banner and pricing can be checked before the server returns one. Prices only
// change for products that carry the offer metafield in Shopify.
const previewOffer = () => {
    if(process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
        return null;
    }
    if(!new URLSearchParams(window.location.search).has('offer_preview')) {
        return null;
    }
    return parseOffer({ title: 'Preview offer', ends_at: new Date(Date.now() + 2 * 86400000).toISOString() });
};

export const useServiceability = () => useContext(ServiceabilityContext);

// Renders children only when the visitor is a dealer-area (no online sales,
// dealer nearby) — used to conditionally show the homepage Consultation block.
export const DealerOnly = ({ children }) => {
    const { status } = useServiceability();
    return status === 'dealer' ? children : null;
};

export const ServiceabilityProvider = ({ utils, children }) => {

    const [status, setStatus] = useState('loading');
    const [modal, setModal] = useState(null); // 'consult' | 'notify' | null
    const [offer, setOffer] = useState(null);
    const [volumePricing, setVolumePricing] = useState(false);
    const [now, setNow] = useState(Date.now());

    // utils is a fresh object each App render — pin it so the check runs once.
    const utilsRef = useRef(utils);
    utilsRef.current = utils;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await Request.post(utilsRef.current, '/shop/', { type: 'is_serviceable_area' });
                if(cancelled) {
                    return;
                }
                setStatus(res.online_sales ? 'online' : (res.dealer_available ? 'dealer' : 'waitlist'));
                setOffer(res.online_sales ? (parseOffer(res.limited_offer) || previewOffer()) : null);
                setVolumePricing(res.online_sales === true && res.volume_pricing === true);
            } catch(_e) {
                if(!cancelled) {
                    setStatus('error'); // fail closed — withhold pricing
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Re-render at the offer's next boundary (start or end) so prices and the
    // banner switch over on time, without waiting for a reload.
    useEffect(() => {
        if(!offer) {
            return;
        }
        const next = [offer.startsAt, offer.endsAt].find(t => t > now);
        if(next == null) {
            return;
        }
        const timer = setTimeout(() => setNow(Date.now()), Math.min(next - now + 50, MAX_TIMEOUT));
        return () => clearTimeout(timer);
    }, [offer, now]);

    // waitlist (and error — dealer unknown) -> notify capture; a confirmed
    // dealer area -> in-home consultation
    const openAvailability = () => setModal((status === 'waitlist' || status === 'error') ? 'notify' : 'consult');

    const value = {
        status,
        canBuyOnline: status === 'online',
        hasDealer: status === 'dealer',
        offer: (status === 'online' && isOfferLive(offer, now)) ? offer : null,
        volumePricing: status === 'online' && volumePricing,
        openAvailability,
    };

    return (
        <ServiceabilityContext.Provider value={value}>
            {children}
            <ConsultationModal
                open={modal === 'consult'}
                onClose={() => setModal(null)}
                utils={utils}
                sourceType={'dealer_consultation'} />
            <NotifyModal
                open={modal === 'notify'}
                onClose={() => setModal(null)}
                utils={utils} />
        </ServiceabilityContext.Provider>
    );
};

export default ServiceabilityProvider;
